const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAY_LABELS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const STATUS_STYLES = {
  scheduled: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  no_show: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS = {
  scheduled: 'Planifié',
  completed: 'Effectué',
  no_show: 'Absent',
  cancelled: 'Annulé',
};

/**
 * Weekly calendar grid for appointments.
 * Desktop: 7-column grid. Mobile: vertical list by day.
 * @param {{ appointments: object[], weekStart: string }} props
 */
export default function AppointmentCalendar({ appointments, weekStart }) {
  const startDate = getMonday(weekStart ? new Date(weekStart) : new Date());
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    return d;
  });

  const byDate = groupByDate(appointments);

  return (
    <>
      {/* Desktop: grid 7 columns */}
      <div className="hidden md:grid grid-cols-7 gap-1 text-xs">
        {DAY_LABELS.map((label) => (
          <div key={label} className="text-center font-semibold text-gray-500 py-1">{label}</div>
        ))}
        {days.map((day) => {
          const dateKey = toDateStr(day);
          const dayAppts = byDate[dateKey] || [];
          const isToday = dateKey === toDateStr(new Date());
          return (
            <div key={dateKey}
              className={`min-h-[80px] border rounded p-1 ${isToday ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
              <div className="text-right text-gray-400 mb-1">{day.getDate()}</div>
              {dayAppts.map((apt) => (
                <div key={apt.id}
                  className={`rounded px-1 py-0.5 mb-0.5 truncate ${STATUS_STYLES[apt.status] || 'bg-gray-100'}`}
                  title={`${apt.client_name} - ${formatTime(apt.scheduled_at)}`}>
                  <span className="font-medium">{formatTime(apt.scheduled_at)}</span> {apt.client_name}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Mobile: vertical list */}
      <div className="md:hidden space-y-2">
        {days.map((day, i) => {
          const dateKey = toDateStr(day);
          const dayAppts = byDate[dateKey] || [];
          const isToday = dateKey === toDateStr(new Date());
          return (
            <div key={dateKey}
              className={`rounded-xl p-3 ${isToday ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-gray-100'}`}>
              <div className="flex justify-between items-center mb-1.5">
                <span className={`text-sm font-semibold ${isToday ? 'text-blue-700' : 'text-[#0f172a]'}`}>
                  {DAY_LABELS_FULL[i]} {day.getDate()}
                </span>
                {isToday && <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded-full font-medium">Aujourd'hui</span>}
              </div>
              {dayAppts.length > 0 ? dayAppts.map((apt) => (
                <div key={apt.id}
                  className={`rounded-lg px-3 py-2 mb-1 text-sm flex justify-between items-center ${STATUS_STYLES[apt.status] || 'bg-gray-100'}`}>
                  <div>
                    <span className="font-medium">{apt.client_name}</span>
                    {apt.scheduled_at && <span className="text-xs ml-2 opacity-70">{formatTime(apt.scheduled_at)}</span>}
                  </div>
                  <span className="text-[10px] font-medium">{STATUS_LABELS[apt.status] || apt.status}</span>
                </div>
              )) : (
                <p className="text-xs text-gray-400">Aucun RDV</p>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toDateStr(d) {
  return d.toISOString().split('T')[0];
}

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function groupByDate(appointments) {
  const map = {};
  for (const apt of appointments) {
    const key = apt.scheduled_at ? apt.scheduled_at.split('T')[0] : '';
    if (!key) continue;
    if (!map[key]) map[key] = [];
    map[key].push(apt);
  }
  for (const key of Object.keys(map)) {
    map[key].sort((a, b) => (a.scheduled_at || '').localeCompare(b.scheduled_at || ''));
  }
  return map;
}
